/**
 * Pure helpers that derive display data (fact tiles, wildcard race,
 * champion path) from the League Cup overview payload.
 */

const num = (value) => (typeof value === "number" ? value : Number(value) || 0);

// Owner name is the headline identifier across the Cup page; fall back to the
// team name when a provider doesn't expose an owner.
const displayName = (obj) => obj?.owner_name || obj?.team_name || "";

const allGroupRows = (groups) =>
    (groups || []).flatMap((group) =>
        (group.table || []).map((row) => ({ ...row, group_name: group.name }))
    );

/**
 * Compute up to six "cup facts" tiles. Facts without enough data (e.g. no
 * knockout scores yet) are omitted.
 * Each fact: { key, value, label, who, accent }
 */
export function computeCupFacts(cup) {
    const facts = [];
    const rows = allGroupRows(cup?.groups);

    // Best / worst single group-stage week
    let best = null;
    let worst = null;
    rows.forEach((row) => {
        Object.entries(row.week_scores || {}).forEach(([week, score]) => {
            const value = num(score);
            if (!best || value > best.value) {
                best = { value, name: displayName(row), week };
            }
            if (!worst || value < worst.value) {
                worst = { value, name: displayName(row), week };
            }
        });
    });
    if (best) {
        facts.push({
            key: "best-week",
            value: best.value.toFixed(2),
            label: "Best Single Week",
            who: `${best.name} · Wk ${best.week}`,
            accent: "bg-green-mid",
        });
    }

    // Best group-stage total
    const topRow = rows.reduce(
        (acc, row) =>
            !acc || num(row.group_points_for) > num(acc.group_points_for)
                ? row
                : acc,
        null
    );
    if (topRow && num(topRow.group_points_for) > 0) {
        facts.push({
            key: "best-group-stage",
            value: num(topRow.group_points_for).toFixed(2),
            label: "Best Group Stage",
            who: displayName(topRow),
            accent: "bg-mustard",
        });
    }

    // Knockout facts
    const scoredMatches = (cup?.bracket || []).flatMap((round) =>
        (round.matches || [])
            .filter((m) => m.home_score != null && m.away_score != null && m.home && m.away)
            .map((m) => ({ ...m, round_label: round.label }))
    );
    let blowout = null;
    let bestRound = null;
    scoredMatches.forEach((match) => {
        const home = num(match.home_score);
        const away = num(match.away_score);
        const margin = Math.abs(home - away);
        if (!blowout || margin > blowout.margin) {
            const winner = home >= away ? match.home : match.away;
            const loser = home >= away ? match.away : match.home;
            blowout = { margin, winner, loser };
        }
        const high = home >= away ? { value: home, team: match.home } : { value: away, team: match.away };
        if (!bestRound || high.value > bestRound.value) {
            bestRound = { ...high, round_label: match.round_label };
        }
    });
    if (blowout) {
        facts.push({
            key: "ko-blowout",
            value: `+${blowout.margin.toFixed(2)}`,
            label: "Biggest KO Blowout",
            who: `${displayName(blowout.winner)} vs ${displayName(blowout.loser)}`,
            accent: "bg-coral",
        });
    }

    // Closest 1-vs-2 race within a group
    let closest = null;
    (cup?.groups || []).forEach((group) => {
        const table = group.table || [];
        if (table.length < 2) return;
        if (!num(table[0].group_points_for) && !num(table[1].group_points_for)) return;
        const gap = num(table[0].group_points_for) - num(table[1].group_points_for);
        if (!closest || gap < closest.gap) {
            closest = { gap, group: group.name, top: table[0], runner: table[1] };
        }
    });
    if (closest) {
        facts.push({
            key: "closest-race",
            value: `${closest.gap.toFixed(2)} pts`,
            label: "Closest Group Race",
            who: `${closest.group} · ${displayName(closest.top)} over ${displayName(closest.runner)}`,
            accent: "bg-green",
        });
    }

    if (bestRound) {
        facts.push({
            key: "best-ko-round",
            value: bestRound.value.toFixed(2),
            label: "Best KO Round",
            who: `${displayName(bestRound.team)} · ${bestRound.round_label}`,
            accent: "bg-forest",
        });
    }

    if (worst && worst.value !== best?.value) {
        facts.push({
            key: "lowest-week",
            value: worst.value.toFixed(2),
            label: "Lowest Cup Week",
            who: `${worst.name} · Wk ${worst.week}`,
            accent: "bg-coral",
        });
    }

    return facts.slice(0, 6);
}

/**
 * The wildcard race: every team that wasn't a direct group qualifier,
 * ranked by group-stage points. Returns null when the cup has no wildcard
 * spots or no scores yet.
 * { entries: [{team_name, owner_name, points, isWildcard, width}], margin }
 */
export function buildWildcardRace(cup) {
    const wildcardCount = num(cup?.schedule?.wildcard_count);
    if (!wildcardCount) return null;

    const advancePerGroup = Math.max(num(cup?.config?.advance_per_group), 1);
    const candidates = (cup?.groups || [])
        .flatMap((group) =>
            (group.table || [])
                .slice(advancePerGroup)
                .map((row) => ({ ...row, group_name: group.name }))
        )
        .sort((a, b) => num(b.group_points_for) - num(a.group_points_for));
    if (!candidates.length || !num(candidates[0].group_points_for)) return null;

    const shown = candidates.slice(0, Math.max(wildcardCount + 3, 4));
    const max = num(shown[0].group_points_for);
    const entries = shown.map((row, index) => ({
        team_name: row.team_name,
        owner_name: row.owner_name,
        points: num(row.group_points_for),
        isWildcard: row.wildcard || (!row.qualified && index < wildcardCount && !cup?.bracket?.length),
        width: max ? Math.max((num(row.group_points_for) / max) * 100, 4) : 0,
    }));

    const lastIn = candidates[wildcardCount - 1];
    const firstOut = candidates[wildcardCount];
    const margin =
        lastIn && firstOut
            ? num(lastIn.group_points_for) - num(firstOut.group_points_for)
            : null;

    return { entries, margin, wildcardCount };
}

/**
 * The champion's run, e.g. "Wildcard → Semifinals +75.72 → Final +58.30".
 * Returns null when there is no champion yet.
 */
export function buildChampionPath(cup) {
    const championId = cup?.champion?.provider_team_id;
    if (!championId) return null;

    const rows = allGroupRows(cup?.groups);
    const championRow = rows.find((row) => row.provider_team_id === championId);
    const origin = championRow?.wildcard
        ? "Wildcard"
        : championRow?.seed
        ? `${championRow.seed} Seed`
        : "Qualifier";

    const legs = (cup?.bracket || [])
        .map((round) => {
            const match = (round.matches || []).find(
                (m) => m.winner_provider_team_id === championId
            );
            if (!match || match.home_score == null || match.away_score == null) {
                return null;
            }
            const margin = Math.abs(num(match.home_score) - num(match.away_score));
            return { label: round.label, margin: margin.toFixed(2) };
        })
        .filter(Boolean);

    return { origin, legs };
}

export const formatWeekRange = (start, end) =>
    start === end ? `Wk ${start}` : `Wk ${start}–${end}`;
